/**
 * From sheet cells + a column mapping to import rows, and from server check
 * results to the status each row shows in Review.
 */
import type { InviteChannel } from '@/api/types';
import { channelLabel } from '@/lib/outreach-copy';
import { cleanEmail, isEmptyAnswer, type ColumnMapping } from './columns';
import { creatorKey, extractHandle } from './handles';

export interface ImportRow {
  /** Position among the data rows (0-based). Stable id for the whole run. */
  key: number;
  /** Row number as the spreadsheet shows it (1-based, header included). */
  rowNumber: number;
  rawHandle: string;
  channel: InviteChannel | null;
  username: string | null;
  /** Why the handle couldn't be read; null when it could. */
  error: string | null;
  /** Key of the first row with the same creator, when this one repeats it. */
  duplicateOf: number | null;
  niche: string;
  email: string | null;
  date: string;
  notes: string;
}

export interface BuildOptions {
  hasHeader: boolean;
  mapping: ColumnMapping;
  /** Platform for plain handles (no link). */
  fallbackChannel: InviteChannel | null;
  /** The team's platforms. A row on any other platform is invalid. */
  allowedChannels: ReadonlyArray<InviteChannel>;
}

function cell(row: string[], col: number | null): string {
  return col === null ? '' : String(row[col] ?? '').trim();
}

export function buildRows(sheetRows: string[][], opts: BuildOptions): ImportRow[] {
  const { mapping } = opts;
  const offset = opts.hasHeader ? 1 : 0;
  const seen = new Map<string, number>();
  const out: ImportRow[] = [];
  sheetRows.slice(offset).forEach((r, i) => {
    // Skip lines with nothing in any mapped column (spacer rows, totals).
    const mapped = [mapping.handle, mapping.niche, mapping.email, mapping.notes].map((c) => cell(r, c));
    if (mapped.every((v) => v === '')) return;

    const rawHandle = cell(r, mapping.handle);
    const parsed = extractHandle(rawHandle, opts.fallbackChannel);
    let channel: InviteChannel | null = parsed.channel;
    let username: string | null = null;
    let error: string | null = null;
    if (!rawHandle) {
      error = 'No handle in this row';
    } else if (!parsed.ok) {
      error = parsed.reason;
    } else if (!opts.allowedChannels.includes(parsed.channel)) {
      error = `Your team doesn't log ${channelLabel(parsed.channel)}`;
      username = parsed.username;
    } else {
      channel = parsed.channel;
      username = parsed.username;
    }

    let duplicateOf: number | null = null;
    if (!error && channel && username) {
      const k = creatorKey(channel, username);
      const first = seen.get(k);
      if (first !== undefined) duplicateOf = first;
      else seen.set(k, out.length);
    }

    const niche = cell(r, mapping.niche);
    const notes = cell(r, mapping.notes);
    out.push({
      key: out.length,
      rowNumber: i + offset + 1,
      rawHandle,
      channel,
      username,
      error,
      duplicateOf,
      niche: isEmptyAnswer(niche) ? '' : niche,
      email: cleanEmail(cell(r, mapping.email)),
      date: cell(r, mapping.date),
      notes: isEmptyAnswer(notes) ? '' : notes,
    });
  });
  return out;
}

/** The platform most links in the file point at: a sensible default for plain handles. */
export function dominantChannel(sheetRows: string[][], hasHeader: boolean, handleCol: number | null): InviteChannel | null {
  if (handleCol === null) return null;
  const counts = new Map<InviteChannel, number>();
  for (const r of sheetRows.slice(hasHeader ? 1 : 0)) {
    const parsed = extractHandle(r[handleCol] ?? '', null);
    if (parsed.ok) counts.set(parsed.channel, (counts.get(parsed.channel) ?? 0) + 1);
  }
  let best: InviteChannel | null = null;
  let n = 0;
  for (const [c, k] of counts) {
    if (k > n) {
      best = c;
      n = k;
    }
  }
  return best;
}

/** Whether any row is a plain handle (so the platform picker matters). */
export function hasPlainHandles(sheetRows: string[][], hasHeader: boolean, handleCol: number | null): boolean {
  if (handleCol === null) return false;
  return sheetRows.slice(hasHeader ? 1 : 0).some((r) => {
    const v = (r[handleCol] ?? '').trim();
    if (!v) return false;
    const asLink = extractHandle(v, null);
    return !asLink.ok && extractHandle(v, 'TIKTOK').ok && !/[/]/.test(v);
  });
}

// ── Server check → status ─────────────────────────────────────────────────

export type CheckStatus = 'new' | 'messaged_by_me' | 'mine' | 'taken' | 'invalid';

export interface CheckResult {
  index: number;
  channel: string;
  username: string | null;
  status: CheckStatus;
  inviteStage?: string | null;
  heldByName?: string | null;
  reason?: string | null;
}

/** What Review shows for a row: the server's verdict plus two local ones. */
export type RowStatus = CheckStatus | 'duplicate' | 'unchecked';

export interface RowCheck {
  status: CheckStatus;
  inviteStage?: string | null;
  heldByName?: string | null;
  reason?: string | null;
}

export function rowStatus(row: ImportRow, check: RowCheck | undefined): RowStatus {
  if (row.error) return 'invalid';
  if (row.duplicateOf !== null) return 'duplicate';
  return check?.status ?? 'unchecked';
}

/** Rows ticked by default: creators nobody has logged and that you can act on. */
export function selectedByDefault(status: RowStatus): boolean {
  return status === 'new' || status === 'messaged_by_me';
}

/** Rows that can never be sent (the checkbox is disabled). */
export function isSelectable(status: RowStatus): boolean {
  return status !== 'invalid' && status !== 'duplicate' && status !== 'unchecked';
}

export const ROW_STATUS_ORDER: RowStatus[] = ['new', 'messaged_by_me', 'mine', 'taken', 'invalid', 'duplicate', 'unchecked'];

export function countStatuses(statuses: ReadonlyArray<RowStatus>): Record<RowStatus, number> {
  const out = Object.fromEntries(ROW_STATUS_ORDER.map((s) => [s, 0])) as Record<RowStatus, number>;
  for (const s of statuses) out[s] += 1;
  return out;
}

/** The creators to check: one per distinct valid creator, in file order. */
export function checkItems(rows: ReadonlyArray<ImportRow>): Array<{ key: number; channel: InviteChannel; targetIdentifier: string }> {
  return rows
    .filter((r) => !r.error && r.duplicateOf === null && r.channel && r.username)
    .map((r) => ({ key: r.key, channel: r.channel!, targetIdentifier: r.username! }));
}

// ── Notes ─────────────────────────────────────────────────────────────────

/** Notes sent with a logged row. The file's date goes here, never as the log date. */
export function importNotes(row: Pick<ImportRow, 'date' | 'notes'>, fileName: string): string {
  const head = row.date ? `Imported ${fileName}, form date ${row.date}` : `Imported ${fileName}`;
  const text = row.notes ? `${head}. ${row.notes}` : head;
  return text.length > 1000 ? `${text.slice(0, 997)}...` : text;
}
